<script setup>
import GetDocumentButton from './GetDocumentButton.vue';
import { ref, computed, reactive, watch } from 'vue';
import { pointedFileProperties } from '@/plugins/utils/S57Catalog.js';
import { toStringCoordinates } from '@/plugins/utils/projection.js';
import { useI18n } from 'vue-i18n';
import copy from 'copy-to-clipboard';
import { toast } from 'vue3-toastify';
import { OPEN_MODULE_EVENT } from '@/plugins/utils/events.js';
import { usePortAllData } from '@/stores/portAllData';
import { storeToRefs } from 'pinia';

const { t } = useI18n();
const store = usePortAllData();
const { setVesselSelected } = store;
const { getModuleAisIsOpen, getModuleReplayIsOpen, getVesselSelected } = storeToRefs(store);

const FEATURE_ID = 'foid';
const FEATURE_TYPE = 'featureType';
const GEOMETRY_INSTRUCTION = 'Geometry Instruction';
const VESSEL_NAME = 'Name'
const SOURCE_PROPERTY = 'Source';
const SYMBOL_INSTRUCTION_PROPERTY = 'Symbol Instruction';
const GROUP = 'group';
const LEVEL = 'level';
const SINGLE_CLICK_DURATION = 250;

const emit = defineEmits(['openGroup', 'closeGroup', 'selectFeature', 'openAis'])

const props = defineProps({
  featureFilter: {
    type: Function,
    default: (f) => f
  },
  showFeatureDetails: {
    type: Boolean,
    default: true
  },
  groupLevelMode : {
    type: Boolean,
    default: false
  },
  ignoredProperty: {
    type: Array,
    default: () => {
      return []
    }
  },
  featureCollection: {
    type: Object,
    required: true
  },
  featureSelected: {
    type: Object,
    default: () => null
  }
})

const selectedFeature = ref(null);
const timer = ref(null);
const groupList = reactive({});

const hideProperties = computed(() => {
  if (props.ignoredProperty.length) return props.ignoredProperty;
  return [GEOMETRY_INSTRUCTION, SYMBOL_INSTRUCTION_PROPERTY];
  //return [GEOMETRY_INSTRUCTION, SOURCE_PROPERTY];
})

const features = computed(() => {
  if (props.groupLevelMode) return sortFeatureByGroupAndLevel()
  else return props.featureCollection.features;
})

const sortFeatureByGroupAndLevel = () => {
  const features = props.featureCollection.features;
  const dictionaryGroupLevel = {};
  for (const feature of features) {
    const props = feature.properties;
    const group = props[GROUP];
    if (group !== undefined) {
      if (dictionaryGroupLevel[group] === undefined) {
        dictionaryGroupLevel[group] = {};
      }
      const level = props[LEVEL];
      if (dictionaryGroupLevel[group][level] === undefined) {
        dictionaryGroupLevel[group][level] = [];
      }
      dictionaryGroupLevel[group][level].push(feature);
    }
  }

  return dictionaryGroupLevel;
}

const isIgnoredProperty = (key) => {
  return hideProperties.value.includes(key) || (props.groupLevelMode && (key === GROUP || key === LEVEL));
}

const isFileAttachment = (key) => {
  return pointedFileProperties.includes(key);
}

const createFeatureTitle = (featureProperties) => {
  let featureTitle;
  switch (featureProperties[FEATURE_TYPE]) {
    case 'Vessel':
      if (featureProperties[VESSEL_NAME]) featureTitle = featureProperties[VESSEL_NAME]
      else featureTitle = featureProperties[FEATURE_TYPE];
      break;
    default:
      featureTitle = featureProperties[FEATURE_TYPE]
      break;
  }
  if (featureProperties[FEATURE_ID]) featureTitle += ` (${featureProperties[FEATURE_ID]})`;
  return featureTitle;
}

const copyText = (key, value) => {
  const text = `${key}: ${value}`;
  copy(text);
  toast(t('copied'));
}

const toggleFeatureProperties = (feature, fitToFeature = true, dispatchEvent = true) => {
  if (timer.value) {
    clearTimeout(timer.value);
    timer.value = undefined;
    selectFeature(feature, fitToFeature, dispatchEvent);
  } else {
    timer.value = setTimeout(() => {
      timer.value = undefined;
      selectFeature(feature, false, dispatchEvent)
    }, SINGLE_CLICK_DURATION);
  }
}

const selectFeature = (feature, fitToFeature = false, dispatchEvent = true) => { 
  if (feature) {
    if (isSelected(feature)) {
      selectedFeature.value = null;
      setVesselSelected(null);
    } else {
      selectedFeature.value = feature;
      if (feature.properties.featureType === 'Vessel') setVesselSelected(feature);
      else setVesselSelected(null);
    }
  } else {
    selectedFeature.value = null;
    setVesselSelected(null);
  }
  if (dispatchEvent) {
    const detail = { selectedFeature: selectedFeature.value, group: null, fitToFeature }
    emit('selectFeature', detail);
  }
}

const isSelected = (feature) => {
  if (selectedFeature.value) {
    let objectAreSame = true;
    const props = selectedFeature.value.properties;
    if (objectAreSame) {
      for (const key of Object.keys(props)) {
        if (props[key] !== feature.properties[key]) {
          objectAreSame = false;
          break;
        }
      }
    }
    return objectAreSame;
  }
  return false;
}

const isHiddenGroup = (group)  => {
  if (group in groupList) return !groupList[group];
  return false;
}

const changeGroupVisibility = (group) => {
  if (group in groupList) groupList[group] = !groupList[group];
  else groupList[group] = false;
  const detail = { selectedFeature: null, group }
  if (groupList[group]) emit('openGroup', detail);
  else emit('closeGroup', detail);
}

const openAisModule = (feature) => {
  if (!selectedFeature.value || !isSelected(feature)) toggleFeatureProperties(feature)
  document.dispatchEvent(new CustomEvent(OPEN_MODULE_EVENT, { detail: { module: 'ais' }}))
  emit('openAis')
}

watch(getVesselSelected, (nv) => {
  if (!nv && selectedFeature.value !== null) {
    selectFeature(null)
  }
})

defineExpose({
  toggleFeatureProperties
})

</script>

<template>
  <div class="feature-collection-panel">
    <template v-if="groupLevelMode">
      <div class="feature-group">
        <div v-for="group in Object.keys(features)" :key="group" class="group" :class="{ hidden: isHiddenGroup(group) }">
          <span class="title-group" @click="changeGroupVisibility(group)">
            <span />
            <span>{{ group }}</span>
          </span>
          <div v-for="level of Object.keys(features[group])" :key="`${group}_${level}`" class="level">
            <span class="title-level">{{ level }}</span>
            <template v-for="feature in features[group][level]" :key="feature">
              <div v-if="featureFilter(feature)" class="feature" :class="{ selected: isSelected(feature) }">
                <button class="feature-id" @click="toggleFeatureProperties(feature)">
                  {{ createFeatureTitle(feature.properties) }}
                </button>
                <div v-if="showFeatureDetails" class="feature-properties">
                  <table>
                    <tbody>
                      <template v-for="[key, value] in Object.entries(feature.properties)" :key="key">
                        <tr v-if="!isIgnoredProperty(key)" @click="copyText(key, value)">
                          <td>{{ key }}</td>
                          <td>
                            <get-document-button v-if="isFileAttachment(key)" :feature-properties="feature.properties" :property-key="key" />
                            <a v-else-if="key === 'Phone Number'" :href="`tel:${value.replace(/\s/g, '')}`">{{ value }}</a>
                            <a v-else-if="key === 'URL'" :href="value" target="_blank">{{ value }}</a>
                            <a v-else-if="key === 'E-mail'" :href="`mailto:${value}`">{{ value }}</a>
                            <span v-else>{{ value }}</span>
                          </td>
                        </tr>
                      </template>
                      <tr v-if="feature.geometry.type === 'Point'" @click="copyText('Coordinates', toStringCoordinates(feature.geometry.coordinates, { dmdDec: 3 }))">
                        <td>Coordinates</td>
                        <td>{{ toStringCoordinates(feature.geometry.coordinates, { dmdDec: 3 }) }}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </template>
          </div>
        </div>
      </div>
    </template>
    <template v-for="feature in features" v-else :key="feature">
      <div v-if="featureFilter(feature)" class="feature" :class="{ selected: isSelected(feature) }">
        <div class="feature-id-container">
          <button class="feature-id" @click="toggleFeatureProperties(feature)">
            {{ createFeatureTitle(feature.properties) }}
          </button>
          <button v-if="feature.properties.featureType === 'Vessel' && !getModuleReplayIsOpen" :class="{ active: isSelected(feature) && getModuleAisIsOpen }" @click="openAisModule(feature)">
            <i class="icon-ais" />
          </button>
        </div>
        <div v-if="showFeatureDetails" class="feature-properties">
          <table>
            <tbody>
              <template v-for="[key, value] in Object.entries(feature.properties)" :key="key">
                <tr v-if="!isIgnoredProperty(key)" @click="copyText(key, value)">
                  <td>{{ key }}</td>
                  <td>
                    <get-document-button v-if="isFileAttachment(key)" :feature-properties="feature.properties" :property-key="key" />
                    <a v-else-if="key === 'Phone Number'" :href="`tel:${value.replace(/\s/g, '')}`">{{ value }}</a>
                    <a v-else-if="key === 'URL'" :href="value" target="_blank">{{ value }}</a>
                    <a v-else-if="key === 'E-mail'" :href="`mailto:${value}`">{{ value }}</a>
                    <span v-else>{{ value }}</span>
                  </td>
                </tr>
              </template>
              <tr v-if="feature.geometry.type === 'Point'" @click="copyText('Coordinates', toStringCoordinates(feature.geometry.coordinates, { dmdDec: 3 }))">
                <td>Coordinates</td>
                <td>{{ toStringCoordinates(feature.geometry.coordinates, { dmdDec: 3 }) }}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </template>
  </div>
</template>

<i18n src="@/locales/featureCollectionPanel.json"></i18n>